import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'constants/endpoints';
import { get, handleError, handleResponse, post, put } from './base.service';
import { useState } from 'react';
import { dataLimits } from 'constants';

export const useCountryListQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_COUNTRY_LIST}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['countryList'],
	});

export const useStateListQuery = (id: number) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_STATE_LIST}/${id}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['stateList'],
		enabled: !!id,
	});

export const useGetCityListQuery = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await post(`${API_ENDPOINTS.GET_CITY_LIST}`, payload);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['cityList'],
		enabled: !!payload?.stateCode && !!payload?.countryCode,
	});

export const useSkillsListQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_SKILLS_LIST}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['skills-list'],
	});

export const useSearchCompaniesQuery = (search: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_COMPANY_SEARCH}?keyword=${search}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['searchCompany'],
		enabled: !!search,
		staleTime: Infinity,
	});

export const useSearchAddress = (payload: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await post(API_ENDPOINTS.GET_LOCATIONS, payload);
				return response?.data ? response.data : [];
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['locations'],
		enabled: !!payload?.keyword,
		staleTime: Infinity,
	});

export const useBucketUrlMutation = () => {
	return useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.GET_MEDIA_SIGN_URL, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useGenerateTokenMutation = () =>
	useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.GET_ZEGO_TOKEN, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});

export const useUploadFileMutation = () =>
	useMutation({
		mutationFn: async (payload: any) => {
			try {
				const res: Response = await fetch(payload?.url, payload?.requestOptions);
				const contentType = res.headers.get?.('content-type') || '';
				let data: any = null;

				if (contentType.includes('application/json')) {
					// Some endpoints may return JSON; parse when available
					try {
						data = await res.json();
					} catch (e) {
						data = null;
					}
				}

				return {
					success: res.ok,
					status: res.status,
					data,
				};
			} catch (error: any) {
				return {
					success: false,
					message: error?.message || 'Upload request failed',
				};
			}
		},
		onSuccess: handleResponse,
	});

export const useIndustriesListQuery = (initialEnabled = true) => {
	const [isQueryEnabled, setIsQueryEnabled] = useState(initialEnabled);
	const enableQuery = () => {
		setIsQueryEnabled(true);
	};
	if (!isQueryEnabled) {
		enableQuery();
	}
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_INDUSTRIES_LIST}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['industries-list'],
		enabled: isQueryEnabled,
	});
};

export const useStaticPagesQuery = () => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_STATIC_PAGES}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['static-pages'],
	});
};

export const useStaticPagesMutation = () => {
	return useMutation({
		mutationFn: () => {
			return get(API_ENDPOINTS.GET_STATIC_PAGES);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useContactUsMutation = () => {
	return useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.CONTACT_US_FORM, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useMultiSearchQuery = (queryParams: any) => {
	return useQuery({
		queryFn: async () => {
			try {
				let response;
				const { page, searchType, keyword, memberQuery, limit } = queryParams;

				if (searchType !== 'member') {
					response = await get(
						`${API_ENDPOINTS.GET_MULTI_SEARCH_RESULT}?page=${page}&limit=${limit || dataLimits?.membersResults}&searchType=${searchType}&keyword=${keyword}`,
					);
				} else {
					const {
						countryId,
						stateId,
						cityId,
						salaryType,
						salary,
						jobRoles,
						skill,
						levelId,
						industry,
					} = memberQuery;

					response = await get(
						`${API_ENDPOINTS.GET_MULTI_SEARCH_RESULT}?industryId=${industry}&countryId=${countryId}&stateId=${stateId}&cityId=${cityId}&salaryType=${salaryType}&salary=${salary}&jobRole=${jobRoles}&skill=${skill}&levelId=${levelId}&page=${page}&limit=${limit || dataLimits?.membersResultsPeople}&searchType=${searchType}&keyword=${keyword}`,
					);
				}
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: [queryParams.searchType],
	});
};

export const useSearchSuggestionsQuery = (queryParams: any) =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.GET_MULTI_SEARCH_RESULT}?${queryParams?.searchStr}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-search-suggetions'],
		refetchOnWindowFocus: false,
		staleTime: Infinity,
		enabled: !!queryParams?.keyword,
	});

export const useGetSeekerFilteredDataQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_SEEKER_FILTER_DATA}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['seeker-filtered-data'],
		refetchOnWindowFocus: false,
	});

export const useFaqQuery = () => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_FAQ}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-faq'],
	});
};

export const useFaqMutation = () => {
	return useMutation({
		mutationFn: () => {
			return get(API_ENDPOINTS.GET_FAQ);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useGetTimeSlotsQuery = () => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.GET_MEETING_TIME_AVAILABILITY}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-time-slots'],
		staleTime: 60000,
	});
};

export const useGetSeekerMeetingListQuery = (payload: any) => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.SEEKER_MEETING_LIST}?order=DESC&page=${payload?.page}&limit=${payload?.limit}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-meeting-list'],
	});
};

export const useSaveSeekerAvailabilityMutation = () => {
	return useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.SAVE_SEEKER_AVAILABILITY, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useGetSeekerAvailabilityQuery = (payload: any) => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.GET_SEEKER_AVAILABILITY}?month=${payload?.month}&year=${payload?.year}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['get-seeker-availability'],
	});
};

export const useGetCompanyMeetingListQuery = (payload: any, currentPage: any) => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.COMPANY_MEETING_LIST}?order=DESC&page=${currentPage}&limit=${payload?.limit}&keyword=${payload.keyword}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['company-meeting-list'],
	});
};

export const useCancelSeekerMeetingMutation = () => {
	return useMutation({
		mutationFn: (payload: any) => {
			return put(API_ENDPOINTS.CANCEL_SEEKER_MEETING, payload); // need {"meetingId" : 'value'}
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useChangeMeetingStatusMutation = () => {
	return useMutation({
		mutationFn: (payload: any) => {
			return put(API_ENDPOINTS.CHANGE_MEETING_STATUS, payload); // need {"meetingId" : 'value', status: "cancelled"} - ["cancelled ", "completed", "pending"]
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useGetCompanySeekerAvailabilityQuery = (payload: any) => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(
					`${API_ENDPOINTS.COMPANY_GET_SEEKER_AVAILABILITY}?month=${payload?.month}&year=${payload?.year}&userId=${payload?.userId}`,
				);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['company-get-seeker-availability'],
	});
};

export const useScheduleInterviewMutation = () => {
	return useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.SCHEDULE_INTERVIEW_WITH_SEEKER, payload); // need {"meetingId" : 'value', status: "cancelled"} - ["cancelled ", "completed", "pending"]
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useSaveGoogleAuthTokenQuery = (payload: any) => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await post(`${API_ENDPOINTS.GET_GOOGLE_AUTH_TOKEN}`, { code: payload });
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['company-get-google-auth-token'],
	});
};

export const useGetGoogleAuthUrlMutation = () => {
	return useMutation({
		mutationFn: () => {
			return get(API_ENDPOINTS.GET_GOOGLE_AUTH_URL);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useRefreshTokenUpdateMutation = () => {
	return useMutation({
		mutationFn: async () => {
			try {
				const response = await get(API_ENDPOINTS.GET_CHECK_GOOGLE_TOKEN);
				return response;
			} catch (err) {
				console.log(err);
			}
		},
	});
};

export const useNotificationListQuery = (payload: any) => {
	return useQuery({
		queryFn: async () => {
			try {
				const response = await get(`${API_ENDPOINTS.NOTIFICATION_LIST}?${payload}`);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		enabled: !!payload,
		queryKey: ['notification-list'],
	});
};

export const useNotificationReadAllMutation = () => {
	return useMutation({
		mutationFn: () => {
			return put(API_ENDPOINTS.NOTIFICATION_READ);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useNotificationReadMutation = () => {
	return useMutation({
		mutationFn: (payload: { id: string }) => {
			return put(`${API_ENDPOINTS.NOTIFICATION_READ_BY_ID}?notificationId=${payload?.id}`);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};

export const useGetHomeVideosQuery = () =>
	useQuery({
		queryFn: async () => {
			try {
				const response = await get(API_ENDPOINTS.GET_HOME_SLIDER);
				return handleResponse(response);
			} catch (err) {
				handleError(err);
			}
		},
		queryKey: ['video-list'],
		refetchOnWindowFocus: false,
		staleTime: Infinity,
	});

export const usePageVisitMutation = () => {
	return useMutation({
		mutationFn: (payload: any) => {
			return post(API_ENDPOINTS.PAGE_VISIT, payload);
		},
		onSuccess: handleResponse,
		onError: handleError,
	});
};
